import type {
  ProductionPlannerPageDto,
  ProductionPlannerRowDto,
  WorkOrderPlanResponseDto,
} from '@artic/api-client';
import type { InfiniteData } from '@tanstack/react-query';

/** The generated page contract, normalized to the shared planner row DTO. */
export type ProductionPlannerPage = Omit<ProductionPlannerPageDto, 'data'> & {
  data: ProductionPlannerRowDto[];
};

export interface ProductionPlannerPageParam {
  offset: number;
  limit: number;
}

export type ProductionPlannerPageData = InfiniteData<
  ProductionPlannerPage,
  ProductionPlannerPageParam
>;

/**
 * Combines rows from all cached production planner pages into a single array.
 *
 * @param cache - The cached production planner pages, if available
 * @returns All rows from the cached pages, or an empty array when no cache exists
 */
export function flattenProductionPlannerRows(
  cache: InfiniteData<ProductionPlannerPage, unknown> | undefined,
): ProductionPlannerRowDto[] {
  return cache?.pages.flatMap((page) => page.data) ?? [];
}

/**
 * Finds the first production planner row with the specified work order ID.
 *
 * @param cache - The cached production planner pages to search
 * @param workOrderId - The work order ID to match
 * @returns The matching row, or `undefined` if no row matches
 */
export function findProductionPlannerRow(
  cache: ProductionPlannerPageData | undefined,
  workOrderId: string,
): ProductionPlannerRowDto | undefined {
  for (const page of cache?.pages ?? []) {
    const row = page.data.find((candidate) => candidate.work_order_id === workOrderId);
    if (row) return row;
  }
  return undefined;
}

/**
 * Map every loaded row while retaining the cache, page, and row references for
 * every untouched branch.
 */
export function mapProductionPlannerRows(
  cache: ProductionPlannerPageData,
  mapper: (row: ProductionPlannerRowDto) => ProductionPlannerRowDto,
): ProductionPlannerPageData {
  let cacheChanged = false;
  const pages = cache.pages.map((page) => {
    let pageChanged = false;
    const data = page.data.map((row) => {
      const nextRow = mapper(row);
      if (nextRow !== row) pageChanged = true;
      return nextRow;
    });

    if (!pageChanged) return page;
    cacheChanged = true;
    return { ...page, data };
  });

  return cacheChanged ? { ...cache, pages } : cache;
}

/**
 * Applies an updater to loaded production planner page data.
 *
 * @param cache - The cached page data, if loaded
 * @param updater - The function that produces updated page data
 * @returns The updated page data, or `undefined` when no cache is loaded
 */
export function applyProductionPlannerPageCacheUpdate(
  cache: ProductionPlannerPageData | undefined,
  updater: (loaded: ProductionPlannerPageData) => ProductionPlannerPageData,
): ProductionPlannerPageData | undefined {
  return cache ? updater(cache) : cache;
}

function isWorkOrderPlanEligible(row: ProductionPlannerRowDto): boolean {
  return (
    row.factory_id !== null &&
    (row.work_order_current_status === 'scheduled' ||
      row.work_order_current_status === 'in_progress')
  );
}

/** Apply a live Plan reading when it is newer than this persisted row. */
export function applyWorkOrderPlanToProductionPlannerRow(
  row: ProductionPlannerRowDto,
  plan: WorkOrderPlanResponseDto,
): ProductionPlannerRowDto {
  if (row.work_order_id !== plan.work_order_id) return row;
  if (!isWorkOrderPlanEligible(row)) return row;
  if (row.plan_computed_at !== null && row.plan_computed_at > plan.computed_at) return row;
  if (
    row.plan_status === plan.plan_status &&
    row.expected_pct_complete === plan.expected_pct_complete &&
    row.plan_computed_at === plan.computed_at
  ) {
    return row;
  }

  return {
    ...row,
    plan_status: plan.plan_status,
    expected_pct_complete: plan.expected_pct_complete,
    plan_computed_at: plan.computed_at,
  };
}

/** Patch one live Plan reading into a loaded planner cache. */
export function applyWorkOrderPlanToProductionPlannerPage(
  cache: ProductionPlannerPageData,
  plan: WorkOrderPlanResponseDto,
): ProductionPlannerPageData {
  return mapProductionPlannerRows(cache, (row) =>
    applyWorkOrderPlanToProductionPlannerRow(row, plan),
  );
}

/** Preserve a newer live Plan verdict when an older persisted page response arrives later. */
export function preserveNewerWorkOrderPlans(
  current: ProductionPlannerPageData | undefined,
  incoming: ProductionPlannerPageData,
): ProductionPlannerPageData {
  if (!current) return incoming;
  const currentRows = new Map(
    flattenProductionPlannerRows(current).map((row) => [row.work_order_id, row]),
  );
  return mapProductionPlannerRows(incoming, (row) => {
    const previous = currentRows.get(row.work_order_id);
    if (
      !previous?.plan_computed_at ||
      !isWorkOrderPlanEligible(row) ||
      (row.plan_computed_at !== null && row.plan_computed_at >= previous.plan_computed_at)
    ) {
      return row;
    }
    return {
      ...row,
      plan_status: previous.plan_status,
      expected_pct_complete: previous.expected_pct_complete,
      plan_computed_at: previous.plan_computed_at,
    };
  });
}
